/**
 * Button コンポーネント
 *
 * 基本的なボタンコンポーネント。
 * バリアント（variant）とサイズ（size）をサポート。
 *
 */

import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/cn';

/**
 * ボタンのバリアント
 */
type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';

/**
 * ボタンのサイズ
 */
type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * Button コンポーネントのProps
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** ボタンのバリアント（デフォルト: 'primary'） */
  variant?: ButtonVariant;

  /** ボタンのサイズ（デフォルト: 'md'） */
  size?: ButtonSize;

  /** 全幅表示 */
  fullWidth?: boolean;
}

/**
 * バリアントごとのスタイル定義
 */
const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-primary',
  secondary:
    'bg-secondary text-secondary-foreground hover:bg-secondary/80 focus-visible:ring-secondary',
  outline:
    'border border-input bg-background hover:bg-accent hover:text-accent-foreground focus-visible:ring-primary',
  ghost:
    'hover:bg-accent hover:text-accent-foreground focus-visible:ring-primary',
};

/**
 * サイズごとのスタイル定義
 */
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-10 px-4 py-2',
  lg: 'h-11 px-8 text-lg',
};

/**
 * Button コンポーネント
 *
 * 使用例:
 * ```tsx
 * <Button variant="primary" size="md" onClick={() => console.log('clicked')}>
 *   クリック
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      fullWidth,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={cn(
          // 基本スタイル
          'inline-flex items-center justify-center gap-2',
          'rounded-md font-medium',
          'transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          // バリアント
          variantStyles[variant],
          // サイズ
          sizeStyles[size],
          // 全幅
          fullWidth && 'w-full',
          // カスタムクラス
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
